import { create } from 'zustand';
import { api } from '../services/api';
import { useAuthStore } from './authStore';
import type { AcesStatus, AcesFinding, AcesFix, AcesBudget, AcesAnomaly } from '../types';

interface AcesState {
  status: AcesStatus | null;
  findings: AcesFinding[];
  fixes: AcesFix[];
  budget: AcesBudget | null;
  anomalies: AcesAnomaly[];
  error: string | null;
  lastFetch: number;

  // Actions
  fetch: () => Promise<void>;
  startPolling: () => () => void;
}

export const useAcesStore = create<AcesState>((set, get) => ({
  status: null,
  findings: [],
  fixes: [],
  budget: null,
  anomalies: [],
  error: null,
  lastFetch: 0,

  fetch: async () => {
    const auth = useAuthStore.getState();
    if (!auth.isAuthenticated && auth.authType !== 'dev_mode') return;

    try {
      const [s, find, fix, bud, anom] = await Promise.all([
        api.getAcesStatus().catch(() => null),
        api.getAcesFindings().catch(() => ({ findings: [], total: 0 })),
        api.getAcesFixes().catch(() => ({ fixes: [], total: 0 })),
        api.getAcesBudget().catch(() => null),
        api.getAcesAnomalies().catch(() => ({ anomalies: [] })),
      ]);
      set({
        status: s as AcesStatus | null,
        findings: find.findings.slice(0, 10),
        fixes: fix.fixes.slice(-5).reverse(),
        budget: bud as AcesBudget | null,
        anomalies: anom.anomalies || [],
        error: null,
        lastFetch: Date.now(),
      });
    } catch (e: unknown) {
      set({ error: (e as Error).message });
    }
  },

  startPolling: () => {
    // Fetch immediately, then every 60s
    get().fetch();
    const interval = setInterval(() => get().fetch(), 60_000);
    return () => clearInterval(interval);
  },
}));
